import React, { useState } from 'react';
import { Tooltip } from '../../../../../components/ds';
import {
  FiSearch,
  FiActivity,
  FiGitCommit,
  FiChevronRight,
  FiLayout,
} from 'react-icons/fi';
import { getNodeAccent } from '../config/nodeVisualConfig';
import {
  AUTHORING_GROUP,
  groupAuthoringItems,
  LOGIC_SUBGROUP,
} from '../utils/nodeUtils';
import {
  DATA_ITEMS,
  MATH_ITEMS,
  OUTPUT_ITEMS,
  POSITIONS,
  VISUAL_ITEMS,
  sectionItems,
} from '../utils/toolboxCatalogue';

// Classification accent for a toolbox item (matches the canvas node palette):
//   chart layouts → main indigo / sub fuchsia; position/signal items → by action
//   (long/TP green, short/SL red, time/exit amber); everything else → by node type.
const itemAccent = (item) => {
    if (item.type === 'chart_output') return item.data?.panel === 'main' ? 'var(--node-comparator)' : 'var(--node-logic)';
    if (item.data?.action) return getNodeAccent('signal', item.data);
    return getNodeAccent(item.type || 'indicator', item.data);
};
const tint = (accent, pct) => `color-mix(in oklch, ${accent} ${pct}%, transparent)`;

// Shared icon chip — accent-tinted background + accent icon, matching the node stripes
const IconChip = ({ item, size = 18, className = '', pad = 'p-2.5' }) => {
    const accent = itemAccent(item);
    const Ico = item.icon || FiActivity;
    return (
        <div
            className={`${pad} rounded-lg group-hover:scale-110 transition-transform ${className}`}
            style={{ color: accent, background: tint(accent, 12) }}
        >
            <Ico size={size} />
        </div>
    );
};

const AccordionGroup = ({ title, icon: Icon, children, defaultOpen = false, forceOpen = false, persistenceKey = null }) => {
    const [isOpen, setIsOpen] = useState(() => {
        if (persistenceKey) {
            try {
                const stored = localStorage.getItem(`deetrade_toolbox_${persistenceKey}`);
                if (stored !== null) return stored === 'true';
            } catch (error) {
                console.warn('localStorage access failed:', error);
            }
        }
        return defaultOpen;
    });

    const effectiveOpen = forceOpen || isOpen;

    const toggleOpen = () => {
        const next = !isOpen;
        setIsOpen(next);
        if (persistenceKey) {
            try {
                localStorage.setItem(`deetrade_toolbox_${persistenceKey}`, String(next));
            } catch (error) {
                console.warn('localStorage set failed:', error);
            }
        }
    };

    return (
        <div className="border-b border-[var(--divider)] last:border-0">
            <button
                onClick={toggleOpen}
                className="w-full flex items-center justify-between px-4 py-3 bg-bg-2 hover:bg-bg-3 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-md bg-bg-3 ${effectiveOpen ? 'text-[var(--color-accent)]' : 'text-fg-3'}`}>
                        <Icon size={16} />
                    </div>
                    <span className="text-xs font-bold text-fg-1 uppercase tracking-wide">{title}</span>
                </div>
                <FiChevronRight size={14} className={`text-fg-3 transition-transform ${effectiveOpen ? 'rotate-90' : ''}`} />
            </button>
            {effectiveOpen && (
                <div className="pb-4 bg-bg-1/30">
                    {children}
                </div>
            )}
        </div>
    );
};

const SectionHeader = ({ title }) => (
    <div className="px-1 mb-4 mt-8 first:mt-0">
        <h5 className="text-[11px] font-black text-fg-3 uppercase tracking-widest">{title}</h5>
    </div>
);

//! D54 visual-gate fix-up — NAMED exports of the two building blocks above so Live Analysis's
//! Indicators tab can reuse the Builder's EXACT accordion/section look (one authority, never a
//! hand-restyled copy). Default export + every existing Builder usage is UNCHANGED (zero
//! behaviour diff — these are additive export statements only).
//--> D56 — subgroup header: "Long Position"/"Short Position" reproduce the Toolbox's own
//*   coloured-bar + h5 markup VERBATIM (two inline divs in this file's Long/Short sections);
//*   every other subgroup (indicator categories, "Math & Logic", the never-guessed "Position" fallback) uses
//*   `SectionHeader` (above).
const LONG_SHORT_BAR_COLOR = { [LOGIC_SUBGROUP.LONG]: 'var(--color-profit)', [LOGIC_SUBGROUP.SHORT]: 'var(--color-loss)' };
const SubgroupHeader = ({ title }) => {
  const barColor = LONG_SHORT_BAR_COLOR[title];
  if (barColor) {
    return (
      <div className="flex items-center gap-3 mb-3">
        <div className="w-1.5 h-4 rounded-full" style={{ background: barColor }} />
        <h5 className="text-[11px] font-black text-fg-3 uppercase tracking-widest">{title}</h5>
      </div>
    );
  }
  return <SectionHeader title={title} />;
};
export { AccordionGroup, SectionHeader, SubgroupHeader };

//! D56 visual-gate fix-up — NAMED export of `POSITIONS` (the Long/Short Position item lists, used
//! both for their per-action icons AND their label text) so Live Analysis's Strategy Logic
//! subgrouping mirrors the Toolbox's taxonomy exactly — one authority, never a second
//! hand-maintained action→icon table. Additive only; every existing Builder usage of the
//! module-local `POSITIONS` binding is unaffected.
export { POSITIONS };

//! D56 — `MATH_LOGIC_ICONS`: a {type: icon} lookup DERIVED from `MATH_ITEMS` (itself derived from
//! `NODE_VISUAL_CONFIG`) — no second hand-maintained icon table for math/comparator/logic_gate.
export const MATH_LOGIC_ICONS = Object.fromEntries(MATH_ITEMS.map((item) => [item.type, item.icon]));

export const ToolboxItemRow = ({ item, compact = false, showDescription = true }) => (
  <div
    data-authoring-item-id={item.id}
    className={`flex items-center ${compact ? 'p-2 rounded-lg' : 'p-3 rounded-xl'}`}
  >
    <IconChip item={item} size={compact ? 16 : 18} pad={compact ? 'p-1.5' : 'p-2.5'} className={compact ? 'mr-3' : 'mr-4'} />
    <div className="flex-1 text-left">
      <span className={`text-xs font-bold text-fg-1 ${compact ? 'uppercase tracking-tight' : 'block mb-0.5'}`}>
        {item.name || item.label}
      </span>
      {showDescription && item.desc && (
        <span className="text-[10px] text-fg-3 font-medium">{item.desc}</span>
      )}
    </div>
  </div>
);

const StrategyToolbox = ({ onItemClick, allIndicators = [], loading = false }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter Logic
  const filterItems = (items) => {
      if (!searchQuery) return items;
      const lowerQuery = searchQuery.toLowerCase();
      return items.filter(item =>
          (item.name || item.label || '').toLowerCase().includes(lowerQuery) ||
          (item.desc || '').toLowerCase().includes(lowerQuery) ||
          (item.category || '').toLowerCase().includes(lowerQuery)
      );
  };

  const chartItems = filterItems(sectionItems('Chart'));
  const dataIds = React.useMemo(() => new Set(DATA_ITEMS.map((item) => item.id)), []);
  const outputIds = React.useMemo(() => new Set(OUTPUT_ITEMS.map((item) => item.id)), []);
  const visualIds = React.useMemo(() => new Set(VISUAL_ITEMS.map((item) => item.id)), []);
  const filteredDataItems = chartItems.filter((item) => dataIds.has(item.id));
  const filteredOutputItems = chartItems.filter((item) => outputIds.has(item.id));
  const filteredVisualItems = chartItems.filter((item) => visualIds.has(item.id));

  const categoryByIndicator = React.useMemo(() => Object.fromEntries(
      allIndicators.map((item) => [item.data?.indicator || item.id, item.category]),
  ), [allIndicators]);
  const authoringItems = [
      ...sectionItems('Not used', allIndicators),
      ...sectionItems('Entry'),
      ...sectionItems('Exit'),
      ...sectionItems('Risk'),
  ];
  const groupedAuthoringItems = groupAuthoringItems(filterItems(authoringItems), categoryByIndicator);
  const indicatorGroups = groupedAuthoringItems.filter((group) => group.group === AUTHORING_GROUP.INDICATORS);
  const strategyLogicGroups = groupedAuthoringItems.filter((group) => group.group === AUTHORING_GROUP.STRATEGY_LOGIC);


  const onDragStart = (event, nodeType, nodeData) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/nodeData', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  const DraggableItem = ({ id, type, data, children, className, title }) => (
    <Tooltip content={title} placement="right">
    <div
        data-authoring-item-id={id}
        draggable
        onDragStart={(e) => onDragStart(e, type, data)}
        onClick={() => onItemClick && onItemClick(type, data)}
        className={`cursor-grab active:cursor-grabbing hover:scale-[1.01] active:scale-95 transition-all duration-200 select-none ${className}`}
    >
        {children}
    </div>
    </Tooltip>
  );

  const cardCls = "bg-bg-2 border border-[var(--divider)] shadow-sm hover:shadow-md hover:border-[var(--border)] transition-all group";

  const renderAuthoringGroup = (group) => (
      <div
        key={group.id}
        data-testid="toolbox-authoring-subgroup"
        data-authoring-group-id={group.id}
        className="mb-4 last:mb-2"
      >
          <SubgroupHeader title={group.title} />
          <div className="space-y-2.5">
              {group.nodes.map((item) => {
                  const compact = item.type === 'indicator';
                  return (
                    <DraggableItem
                      key={item.id}
                      type={item.type}
                      data={item.data}
                      title={item.name || item.label}
                      className={`rounded-xl ${cardCls}`}
                    >
                      <ToolboxItemRow item={item} compact={compact} showDescription={!compact} />
                    </DraggableItem>
                  );
              })}
          </div>
      </div>
  );

  return (
    <div className="flex flex-col h-full bg-bg-2">
        {/* Search Bar */}
        <div className="p-4 border-b border-[var(--divider)] bg-bg-2">
             <div className="relative group">
                <FiSearch className="absolute left-3 top-3 text-fg-3 group-focus-within:text-[var(--color-accent)] transition-colors" />
                <input
                  type="text"
                  placeholder="Search components..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-bg-3 rounded-xl text-sm font-medium text-fg-1 placeholder:text-fg-3 border border-transparent focus:ring-2 focus:border-transparent outline-none transition-all shadow-sm"
                  style={{ '--tw-ring-color': 'color-mix(in oklch, var(--color-accent) 30%, transparent)' }}
                />
             </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">

             {/* 1. VISUALIZATION GROUP */}
             <AccordionGroup title="Visualization" icon={FiLayout} defaultOpen={true} forceOpen={!!searchQuery} persistenceKey="viz">
                 {/* Data Source */}
                 {filteredDataItems.length > 0 && (
                     <div className="px-4 pt-4">
                         <SectionHeader title="Data & Input" />
                         <div className="space-y-3 mb-6">
                             {filteredDataItems.map(item => (
                                <DraggableItem key={item.id} {...item} className={`flex items-center p-3 rounded-xl ${cardCls}`}>
                                    <IconChip item={item} className="mr-4" />
                                    <div className="flex-1 text-left">
                                        <span className="text-xs font-bold text-fg-1 block mb-0.5">{item.name}</span>
                                        <span className="text-[10px] text-fg-3 font-medium">{item.desc}</span>
                                    </div>
                                </DraggableItem>
                             ))}
                         </div>
                     </div>
                 )}

                 {/* Chart Layouts */}
                 {filteredOutputItems.length > 0 && (
                     <div className="px-4">
                         <SectionHeader title="Chart Layouts" />
                         <div className="grid grid-cols-2 gap-3 mb-6">
                             {filteredOutputItems.map(item => (
                                <DraggableItem key={item.id} {...item} className={`flex flex-col items-center justify-center p-3 rounded-xl aspect-[1.3/1] ${cardCls}`}>
                                    <IconChip item={item} pad="p-2" className="mb-1.5 rounded-xl" />
                                    <span className="text-[10px] font-bold text-fg-1 uppercase tracking-tighter text-center leading-tight">{item.name}</span>
                                </DraggableItem>
                             ))}
                         </div>
                     </div>
                 )}

                 {/* Chart Types */}
                 {filteredVisualItems.length > 0 && (
                     <div className="px-4">
                         <SectionHeader title="Chart Types" />
                         <div className="grid grid-cols-1 gap-2 mb-2">
                             {filteredVisualItems.map(item => (
                                <DraggableItem key={item.id} {...item} className={`flex items-center p-2 rounded-lg ${cardCls}`}>
                                  <IconChip item={item} size={16} pad="p-1.5" className="mr-3" />
                                  <span className="text-xs font-bold text-fg-1 uppercase tracking-tight">{item.name}</span>
                                </DraggableItem>
                             ))}
                         </div>
                     </div>
                 )}
             </AccordionGroup>

             {/* 2. INDICATORS GROUP */}
             <AccordionGroup title="Indicators" icon={FiActivity} forceOpen={!!searchQuery} persistenceKey="indicators">
                 <div className="px-4 pt-4">
                     {loading ? (
                         <div className="text-center py-6 text-xs font-medium text-fg-3">Loading indicators...</div>
                     ) : (
                         indicatorGroups.length > 0 ? (
                             indicatorGroups.map(renderAuthoringGroup)
                         ) : (
                             <div className="text-center py-4 text-xs text-fg-3">No indicators found</div>
                         )
                     )}
                 </div>
             </AccordionGroup>

             {/* 3. STRATEGY LOGIC GROUP */}
             <AccordionGroup title="Strategy Logic" icon={FiGitCommit} forceOpen={!!searchQuery} persistenceKey="logic">
                 <div className="px-4 pt-4">
                     {strategyLogicGroups.map(renderAuthoringGroup)}
                 </div>
             </AccordionGroup>
        </div>
    </div>
  );
};

export default React.memo(StrategyToolbox);
